import { Target } from "lucide-react";

/**
 * PenaltyShootout — Iter 247.
 *
 * Renders the penalty-shoot-out score + per-kick dot matrix during a
 * live fixture with status == "P" (or after a knockout that finished
 * in penalties).  Data comes from `live.penalty`:
 *
 *   {
 *     home_score: number,
 *     away_score: number,
 *     kicks: [{
 *       team_side: "home" | "away",
 *       team_name: string,
 *       player: string | null,
 *       scored: boolean,
 *       round: number,
 *     }],
 *   }
 *
 * Visual language:
 *   • Filled green dot  = scored
 *   • Open red dot      = missed
 *   • Hollow grey dot   = pending kick (within first 5 rounds)
 *   • Tabular nums for the running score
 *   • Sudden-death rounds (>5) render inline after a small divider
 *
 * Designed to live inside the live-header-card, just below the LMI
 * bars.  Kept entirely self-contained; no external state.
 */

const SLOT_COUNT = 5; // standard shoot-out has 5 kicks per side
const ACCENT_GREEN = "#22c55e";
const ACCENT_RED = "#ef4444";
const ACCENT_AMBER = "#f59e0b";

const Dot = ({ kick, pending, nextUp }) => {
  // Pending slot in the standard 5-round grid.
  if (pending) {
    return (
      <span
        data-testid="pen-slot-pending"
        className={`inline-block w-3.5 h-3.5 rounded-full border ${
          nextUp
            ? "border-amber-400 animate-pulse"
            : "border-foreground/20"
        }`}
        style={nextUp ? { boxShadow: `0 0 8px ${ACCENT_AMBER}66` } : undefined}
        aria-label={nextUp ? "Next kick" : "Pending kick"}
        title={nextUp ? "Next kick" : "Pending"}
      />
    );
  }
  // Taken kick — filled (scored) or open with cross (missed).
  const scored = !!kick?.scored;
  return (
    <span
      data-testid={scored ? "pen-slot-scored" : "pen-slot-missed"}
      className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full"
      style={{
        background: scored ? ACCENT_GREEN : "transparent",
        border: `1.5px solid ${scored ? ACCENT_GREEN : ACCENT_RED}`,
        boxShadow: scored ? `0 0 10px ${ACCENT_GREEN}55` : undefined,
      }}
      title={
        kick?.player
          ? `${kick.player} — ${scored ? "Scored" : "Missed"} (Round ${kick.round})`
          : scored ? "Scored" : "Missed"
      }
    >
      {!scored && (
        <svg
          viewBox="0 0 10 10"
          className="w-2 h-2"
          fill="none"
          aria-hidden
        >
          <path
            d="M2 2 L8 8 M8 2 L2 8"
            stroke={ACCENT_RED}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </span>
  );
};

const TeamRow = ({
  label,
  flagEmoji,
  score,
  otherScore,
  kicks,
  isNextSide,
  maxRound,
  testid,
}) => {
  // Build the row of slots: first SLOT_COUNT regular slots, then any
  // sudden-death rounds (round > 5) on the right of a thin divider.
  const inRegulation = [];
  const inSuddenDeath = [];
  for (let r = 1; r <= SLOT_COUNT; r += 1) {
    const k = kicks.find((kk) => kk.round === r);
    inRegulation.push({ round: r, kick: k });
  }
  const suddenDeathRounds = Math.max(maxRound - SLOT_COUNT, 0);
  for (let r = SLOT_COUNT + 1; r <= SLOT_COUNT + suddenDeathRounds; r += 1) {
    const k = kicks.find((kk) => kk.round === r);
    inSuddenDeath.push({ round: r, kick: k });
  }
  // Find the next pending slot for this side — the first round (going
  // up) without a recorded kick.
  const sideNextRound = (() => {
    if (!isNextSide) return -1;
    for (let r = 1; r <= Math.max(SLOT_COUNT, maxRound + 1); r += 1) {
      const k = kicks.find((kk) => kk.round === r);
      if (!k) return r;
    }
    return -1;
  })();

  return (
    <div
      className="flex items-center justify-between gap-3"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-base" aria-hidden>{flagEmoji}</span>
        <span className="text-sm font-semibold text-foreground truncate">{label}</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-1.5">
          {inRegulation.map(({ round, kick }) => (
            <Dot
              key={`reg-${round}`}
              kick={kick}
              pending={!kick}
              nextUp={isNextSide && round === sideNextRound}
            />
          ))}
          {inSuddenDeath.length > 0 && (
            <span className="mx-1 h-3 w-px bg-foreground/15" aria-hidden />
          )}
          {inSuddenDeath.map(({ round, kick }) => (
            <Dot
              key={`sd-${round}`}
              kick={kick}
              pending={!kick}
              nextUp={isNextSide && round === sideNextRound}
            />
          ))}
        </div>
        <span
          className="font-tabular font-bold text-2xl tabular-nums min-w-[1.5rem] text-right"
          style={{
            color: score > otherScore ? ACCENT_GREEN : undefined,
          }}
          data-testid={`${testid}-score`}
        >
          {score}
        </span>
      </div>
    </div>
  );
};

export const PenaltyShootout = ({
  penalty,
  homeTeam,
  awayTeam,
  homeFlag = "",
  awayFlag = "",
  status,
}) => {
  if (!penalty) return null;
  const kicks = Array.isArray(penalty.kicks) ? penalty.kicks : [];
  const homeKicks = kicks.filter((k) => k.team_side === "home");
  const awayKicks = kicks.filter((k) => k.team_side === "away");
  const maxRound = Math.max(
    homeKicks.length,
    awayKicks.length,
    SLOT_COUNT, // always reserve 5 regulation slots
  );

  // Determine which side kicks next.  Convention: teams alternate; the
  // side with fewer kicks taken kicks next.  If both have the same
  // count (and shoot-out isn't over), the away side typically kicks
  // first into the round.  We default to "home" when both are zero.
  const isLive = status === "P";
  let nextSide = null;
  if (isLive) {
    if (homeKicks.length < awayKicks.length) nextSide = "home";
    else if (awayKicks.length < homeKicks.length) nextSide = "away";
    else nextSide = "home"; // tie → home starts next round
  }

  return (
    <div
      className="mt-6 pt-5 border-t border-border"
      data-testid="penalty-shootout"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-amber-400" />
          <span
            className="text-xs font-bold uppercase tracking-[0.18em] text-amber-400"
            data-testid="penalty-shootout-title"
          >
            Penalty Shootout
          </span>
          {isLive && (
            <span
              className="ml-1 w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"
              aria-hidden
            />
          )}
        </div>
        <div
          className="font-tabular text-xs text-muted-foreground tabular-nums"
          data-testid="penalty-round-label"
        >
          Round {Math.max(homeKicks.length, awayKicks.length, 1)}
          {maxRound > SLOT_COUNT && " · Sudden death"}
        </div>
      </div>

      <div className="space-y-3.5">
        <TeamRow
          label={homeTeam}
          flagEmoji={homeFlag}
          score={penalty.home_score || 0}
          otherScore={penalty.away_score || 0}
          kicks={homeKicks}
          isNextSide={nextSide === "home"}
          maxRound={maxRound}
          testid="penalty-row-home"
        />
        <TeamRow
          label={awayTeam}
          flagEmoji={awayFlag}
          score={penalty.away_score || 0}
          otherScore={penalty.home_score || 0}
          kicks={awayKicks}
          isNextSide={nextSide === "away"}
          maxRound={maxRound}
          testid="penalty-row-away"
        />
      </div>

      {isLive && nextSide && (
        <div
          className="mt-4 text-[11px] uppercase tracking-wider text-amber-300/80 font-semibold"
          data-testid="penalty-next-up"
        >
          ▸ {(nextSide === "home" ? homeTeam : awayTeam)} to kick
        </div>
      )}
    </div>
  );
};

export default PenaltyShootout;
